import { VisuallyHidden } from "@mantine/core";
import { Image, Video } from "lucide-react";
import { useState } from "react";

import type { TemplateCard as Template } from "../../api/generated/model";
import { TemplateCover, templateCover } from "../../ui/TemplateCover";
import classes from "./templates.module.css";
import { TierBolts } from "./TemplateCost";

export interface TemplateCardProps {
  template: Template;
  /** Opens the template's preview. A Coming soon card opens nothing. */
  onOpen: () => void;
}

/**
 * Live's template card (`template-card`, captures 01, 02 and 43): one 3:4 button named after the
 * template, its cover (`ui/TemplateCover`, as Pro's cards draw it: a video template's card
 * rendition looping muted over its first frame, loaded only on screen, #828 bug 17 and spec
 * ui-perf/template-card-previews; else a picture; a grey tile with the type's icon until the
 * covers are imported), the title over a scrim, and the type's frosted badge at the top left, its
 * tier bolts at the top right (G14-template-costs). It grows on hover and on keyboard focus. One of
 * live's Coming soon cards wears live's pill instead of the badge and is disabled, as live's: it
 * opens nothing.
 */
export function TemplateCard({ template, onOpen }: TemplateCardProps) {
  // The scrim and the title wait for the cover's still, as live's; a card with none is ready.
  const [loaded, setLoaded] = useState(templateCover(template).kind === "icon");
  const video = template.media === "video";
  const Icon = video ? Video : Image;
  return (
    <button
      type="button"
      className={classes.card}
      aria-label={template.title}
      disabled={template.coming_soon}
      data-loading={!loaded || undefined}
      onClick={onOpen}
    >
      <TemplateCover
        template={template}
        className={classes.cover}
        iconClassName={classes.fallback}
        onReady={() => setLoaded(true)}
      />
      <span className={classes.scrim} />
      <span className={classes.content}>
        <span className={classes.cardTop}>
          {template.coming_soon ? (
            <span className={classes.soonPill}>Coming soon</span>
          ) : (
            <span className={classes.badge}>
              <Icon size={14} aria-hidden />
              <VisuallyHidden>{video ? "Video template" : "Image template"}</VisuallyHidden>
            </span>
          )}
          {/* Live's tier bolts, from its declared cost of one output; none without one. */}
          {template.estimate === null || template.estimate === undefined || template.coming_soon ? null : (
            <TierBolts estimate={template.estimate} />
          )}
        </span>
        <span className={classes.title}>{template.title}</span>
      </span>
    </button>
  );
}
